/**
 * useSubscription Hook - Trade Loop v2.0
 *
 * Provides access to user's subscription plan, limits, and usage statistics
 * with React Query caching for optimal performance.
 *
 * Features:
 * - Subscription plan details
 * - Usage statistics (referrals, connections)
 * - Feature access checking
 * - Limit checking with warnings
 * - Auto-refresh on focus
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { SUBSCRIPTION_PLANS, PLAN_VISIBILITY } from '@/config/subscription-plan-limits';
import { timezoneHeaders } from '@/utils/timezone';

// ============================================================================
// TYPES
// ============================================================================

interface FeatureFlags {
  professionalProfile: boolean;
  verifiedBadge: boolean;
  unlimitedTrades: boolean;
  advancedAnalytics: boolean;
  partnerFavorites: boolean;
  availabilityRequests: boolean;
  prioritySupport: boolean;
  teamCollaboration: boolean;
  customIntegrations: boolean;
  dedicatedAccountManager: boolean;
  premiumSupport: boolean;
}

interface SubscriptionPlan {
  name: string;
  description: string;
  monthlyReferrals: number;
  totalConnections: number;
  priceUSD: number;
  billingCycle: 'monthly' | 'free';
  features: string[];
  featureFlags: FeatureFlags;
}

interface UsageStats {
  plan: string;
  limits: {
    monthlyReferrals: number;
    totalConnections: number;
    tradesLimit?: number;
  };
  unlimited?: {
    monthlyReferrals: boolean;
    totalConnections: boolean;
    trades?: boolean;
  };
  usage: {
    monthlyReferrals: number;
    totalConnections: number;
  };
  connectionBreakdown?: {
    accepted: number;
    pending: number;
  };
  usagePercentage: {
    monthlyReferrals: number;
    totalConnections: number;
  };
  warnings: {
    referralsNearLimit: boolean;
    connectionsNearLimit: boolean;
  };
}

interface SubscriptionData extends UsageStats {
  planDetails: SubscriptionPlan;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for accessing subscription data and checking feature access
 *
 * @example
 * function MyComponent() {
 *   const { data, hasFeature, isAtLimit, isNearLimit } = useSubscription();
 *
 *   if (hasFeature('advancedAnalytics')) {
 *     return <AdvancedAnalytics />;
 *   }
 *
 *   if (isAtLimit('monthlyReferrals')) {
 *     return <UpgradePrompt />;
 *   }
 *
 *   return <div>Referrals: {data?.usage.monthlyReferrals} / {data?.limits.monthlyReferrals}</div>;
 * }
 */
export function useSubscription() {
  const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8080";

  // Fetch subscription and usage data
  const query: UseQueryResult<SubscriptionData, Error> = useQuery({
    queryKey: ['subscription', 'usage'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/workflows/dashboard`, {
        credentials: 'include',
        // Raw fetch, so the header lib/api.ts adds automatically has to be added by hand — the
        // usage counts below are windowed on the caller's calendar month (see utils/timezone.ts).
        headers: timezoneHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch subscription data');
      }

      const dashboardData = await response.json();

      // Dashboard endpoint returns usage stats at root level
      const usageStats: UsageStats = dashboardData.usageStats;

      // Import plan config to get full plan details
      // For now, we'll construct this from the usage stats
      const planDetails: SubscriptionPlan = {
        name: getPlanDisplayName(usageStats.plan),
        description: getPlanDescription(usageStats.plan),
        monthlyReferrals: usageStats.limits.monthlyReferrals,
        totalConnections: usageStats.limits.totalConnections,
        priceUSD: getPlanPrice(usageStats.plan),
        billingCycle: usageStats.plan === 'starter' ? 'free' : 'monthly',
        features: getPlanFeatures(usageStats.plan),
        featureFlags: getPlanFeatureFlags(usageStats.plan)
      };

      return {
        ...usageStats,
        planDetails
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchInterval: 30 * 1000 // Refetch every 30 seconds
  });

  // Helper: Check if user has a specific feature
  const hasFeature = (featureName: keyof FeatureFlags): boolean => {
    return query.data?.planDetails.featureFlags[featureName] ?? false;
  };

  // Helper: Check if a resource is unlimited
  const isUnlimited = (limitType: 'monthlyReferrals' | 'totalConnections'): boolean => {
    if (!query.data) return false;
    // Check the unlimited flags from API, or check if limit is Infinity
    return query.data.unlimited?.[limitType] ?? !Number.isFinite(query.data.limits[limitType]);
  };

  // Helper: Check if user is at a limit
  // Note: Unlimited resources always return false
  const isAtLimit = (limitType: 'monthlyReferrals' | 'totalConnections'): boolean => {
    if (!query.data) return false;
    if (isUnlimited(limitType)) return false;
    return query.data.usage[limitType] >= query.data.limits[limitType];
  };

  // Helper: Check if user is near a limit (>= 80%)
  // Note: Unlimited resources always return false
  const isNearLimit = (limitType: 'monthlyReferrals' | 'totalConnections'): boolean => {
    if (!query.data) return false;
    if (isUnlimited(limitType)) return false;
    return query.data.usagePercentage[limitType] >= 80;
  };

  // Helper: Get remaining usage
  // Note: Unlimited resources return Infinity
  const getRemaining = (limitType: 'monthlyReferrals' | 'totalConnections'): number => {
    if (!query.data) return 0;
    if (isUnlimited(limitType)) return Infinity;
    return Math.max(0, query.data.limits[limitType] - query.data.usage[limitType]);
  };

  // Helper: Check if user can perform action
  // Note: Unlimited resources always return true
  const canPerformAction = (limitType: 'monthlyReferrals' | 'totalConnections'): boolean => {
    if (isUnlimited(limitType)) return true;
    return !isAtLimit(limitType);
  };

  // Helper: Get list of available plans (filtered by visibility)
  const getAvailablePlans = () => {
    const plans = Object.entries(SUBSCRIPTION_PLANS)
      .filter(([key]) => {
        // Filter out plans that are not visible
        if (key === 'grow' && !PLAN_VISIBILITY.grow) {
          return false;
        }
        if (key === 'pro' && !PLAN_VISIBILITY.pro) {
          return false;
        }
        if (key === 'starter' && !PLAN_VISIBILITY.starter) {
          return false;
        }
        return true;
      })
      .map(([key, plan]) => ({
        key,
        ...plan
      }));
    return plans;
  };

  return {
    // Query state
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,

    // Helper methods
    hasFeature,
    isUnlimited,
    isAtLimit,
    isNearLimit,
    getRemaining,
    canPerformAction,
    getAvailablePlans,

    // Convenient aliases
    plan: query.data?.plan,
    planDetails: query.data?.planDetails,
    usage: query.data?.usage,
    limits: query.data?.limits,
    unlimited: query.data?.unlimited,
    connectionBreakdown: query.data?.connectionBreakdown,
    usagePercentage: query.data?.usagePercentage,
    warnings: query.data?.warnings,

    // Feature flags with safe default (Starter plan flags when loading)
    // This prevents crashes when components destructure featureFlags before data loads
    featureFlags: query.data?.planDetails?.featureFlags ?? getPlanFeatureFlags('starter')
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getPlanDisplayName(plan: string): string {
  const names: Record<string, string> = {
    starter: 'Starter',
    pro: 'Pro',
    grow: 'Grow'
  };
  return names[plan] || 'Unknown';
}

function getPlanDescription(plan: string): string {
  const descriptions: Record<string, string> = {
    starter: 'Get started with unlimited referrals and basic profile',
    pro: 'Stand out with professional profile and verified badge',
    grow: 'Scale your team with organization-wide features'
  };
  return descriptions[plan] || '';
}

function getPlanPrice(plan: string): number {
  // Note: This is a fallback for backwards compatibility
  // Real-time pricing should use the usePricing() hook instead
  const prices: Record<string, number> = {
    starter: 0,
    pro: 29,
    grow: 79
  };
  return prices[plan] || 0;
}

function getPlanFeatures(plan: string): string[] {
  const features: Record<string, string[]> = {
    starter: [
      'Unlimited referrals & connections',
      'Basic profile',
      '1 trade',
      'Community support'
    ],
    pro: [
      'Everything in Starter',
      'Professional profile',
      'Verified badge',
      'Unlimited trades',
      'Partner lists',
      'Priority support'
    ],
    grow: [
      'Everything in Pro',
      'Team management',
      'Organization-wide accounts',
      'Custom integrations',
      'Dedicated account manager',
      '24/7 premium support'
    ]
  };
  return features[plan] || [];
}

function getPlanFeatureFlags(plan: string): FeatureFlags {
  const flags: Record<string, FeatureFlags> = {
    starter: {
      professionalProfile: false,
      verifiedBadge: false,
      unlimitedTrades: false,
      advancedAnalytics: false,
      partnerFavorites: false,
      availabilityRequests: false,
      prioritySupport: false,
      teamCollaboration: false,
      customIntegrations: false,
      dedicatedAccountManager: false,
      premiumSupport: false
    },
    pro: {
      professionalProfile: true,
      verifiedBadge: true,
      unlimitedTrades: true,
      advancedAnalytics: true,
      partnerFavorites: true,
      availabilityRequests: true,
      prioritySupport: true,
      teamCollaboration: false,
      customIntegrations: false,
      dedicatedAccountManager: false,
      premiumSupport: false
    },
    grow: {
      professionalProfile: true,
      verifiedBadge: true,
      unlimitedTrades: true,
      advancedAnalytics: true,
      partnerFavorites: true,
      availabilityRequests: true,
      prioritySupport: true,
      teamCollaboration: true,
      customIntegrations: true,
      dedicatedAccountManager: true,
      premiumSupport: true
    }
  };
  return flags[plan] || flags.starter;
}
